/* eslint-disable max-len */
import React, { ReactNode, useState } from 'react';
import { shareReportApi, ShareReportParams } from 'API/share-report-api/share-report-api';
import { ShareConfirmationModal } from 'ui-components/share-report-modal/share-confirmation-modal/share-confirmation-modal';
import { ShareModal } from 'ui-components/share-report-modal/share-modal/share-modal';
import { getUtmTrackingParams } from 'tracking/utm-tracking/utm-tracking';
import { addNextUrlForQuery, addQueryParamsToPath } from 'utils/url-query-params/url-query-params';
import { UtmCampaign } from 'tracking/utm-tracking/types';
import { getAppApiEndpoint } from 'core/default-endpoint-url';

export type HeaderTextProps = {
    primaryText: string;
    secondaryText: string;
    tertiaryText: string;
};

export type ShareModalPayload = Omit<ShareReportParams, 'recipient_emails' | 'message'>;
export type ShareModalSharedProps = {
    headerContent: ReactNode | HeaderTextProps;
    visible: boolean;
    onCloseModal: () => void;
    contentBottomElement?: ReactNode;
};

type ShareModalProps = ShareModalSharedProps & {
    sharePayload: ShareModalPayload;
    onSendButtonClick?: () => void;
    onCancelButtonClick?: () => void;
    utmCampaign: UtmCampaign;
};

type ModalToShow = 'shareModal' | 'confirmationModal';

export const MODAL_WIDTH = 480;

export const SHARE_ID_BE_TOKEN = '{SHARE_ID}';

export const ShareReportModal = ({
    headerContent,
    contentBottomElement,
    visible,
    onCloseModal,
    sharePayload,
    onSendButtonClick,
    onCancelButtonClick,
    utmCampaign,
}: ShareModalProps) => {
    const [modalToShow, setModalToShow] = useState<ModalToShow>('shareModal');
    const [isSendingRequest, setIsSendingRequest] = useState<boolean>(false);

    const onModalClosed = () => {
        setModalToShow('shareModal');
        setIsSendingRequest(false);
        onCloseModal();
    };

    const cancelButtonClick = () => {
        onCancelButtonClick && onCancelButtonClick();
        onModalClosed();
    };

    const onShareReport = async (validEmailsList: string[], messageContent: string) => {
        const utmTrackingParams = getUtmTrackingParams({
            utmSource: 'dashboard',
            utmMedium: 'share',
            utmCampaign,
        });

        const paramsToAdd = `share_id=${SHARE_ID_BE_TOKEN}&${utmTrackingParams}`;
        let relativePath = sharePayload.relative_path;
        relativePath = addQueryParamsToPath(relativePath, paramsToAdd);
        relativePath = addNextUrlForQuery('/auth/signup', `${getAppApiEndpoint()}${relativePath}`, [
            SHARE_ID_BE_TOKEN,
        ]);

        const params: ShareReportParams = {
            recipient_emails: validEmailsList,
            message: messageContent,
            ...sharePayload,
            //add a {SHARE_ID} placeholder to be replaced with actual shareId from server
            relative_path: relativePath,
        };
        onSendButtonClick && onSendButtonClick();
        try {
            setIsSendingRequest(true);
            await shareReportApi.createSharedReport(params);
            setIsSendingRequest(false);
            setModalToShow('confirmationModal');
        } catch (error) {
            console.log(error);
            onModalClosed();
        }
    };

    return (
        <div>
            <ShareModal
                visible={visible && modalToShow === 'shareModal'}
                onCloseModal={onModalClosed}
                onCancelButtonClick={cancelButtonClick}
                onShareReport={onShareReport}
                headerContent={headerContent}
                contentBottomElement={contentBottomElement}
                isSendingRequest={isSendingRequest}
            />
            <ShareConfirmationModal
                visible={visible && modalToShow === 'confirmationModal'}
                onCloseModal={onModalClosed}
            />
        </div>
    );
};
